import { Link, useLocation } from 'react-router-dom'
import { useEffect, useState, useCallback } from 'react'
import { SparklesIcon } from '@heroicons/react/24/solid'
import { MAX_YEAR, THEMATA_YEAR, SCHEDULE_YEAR } from './constants.js'

function Header() {
  const location = useLocation()
  const path = location.pathname
  
  const isScores = path.startsWith('/vaseis')
  const isAbout = path === '/about'
  const isExams = path.startsWith('/themata')
  const isSchedule = path.startsWith('/programma')
  const isPomodoro = path.startsWith('/pomodoro')
  const isCalculator = path.startsWith('/ypologismos-morion')
  const isTargets = path.startsWith('/stoxoi-sxolon')
  const isAi = path.startsWith('/ai-symvoulos')
  const isApp = path.startsWith('/app')

  const [open, setOpen] = useState(false)

  // Close menu on route change
  useEffect(() => setOpen(false), [location.pathname])

  // Lock body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [open])

  // Close on Escape
  const onKeyDown = useCallback((e) => { if (e.key === 'Escape') setOpen(false) }, [])
  useEffect(() => {
    if (!open) return
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [open, onKeyDown])

  // Με εννιά συνδέσμους το μενού χωράει σε μία γραμμή στα 1024 με 8px
  // περιθώριο, που είναι πολύ λίγο. Λίγο στενότερο padding μέχρι τα 1280
  // δίνει αέρα χωρίς να αλλάξει καμία ετικέτα.
  const linkBase = 'px-2.5 xl:px-3 py-2 rounded-lg transition-colors text-sm cursor-pointer'
  const active = 'text-gray-700 bg-gray-100 font-medium'
  const idle = 'text-gray-700 hover:text-gray-700'

  const LINKS = [
    { to: '/ai-symvoulos', label: 'AI Σύμβουλος', active: isAi, ai: true },
    { to: `/vaseis/${MAX_YEAR}?ordering=-score`, label: 'Βάσεις', active: isScores },
    { to: `/themata/${THEMATA_YEAR}`, label: 'Θέματα', active: isExams },
    { to: `/programma/${SCHEDULE_YEAR}`, label: 'Πρόγραμμα', active: isSchedule },
    { to: '/pomodoro', label: 'Χρονόμετρο', active: isPomodoro },
    { to: '/stoxoi-sxolon', label: 'Στόχοι', active: isTargets },
    { to: '/ypologismos-morion', label: 'Υπολογισμός', active: isCalculator },
    { to: '/app', label: 'Εφαρμογή', active: isApp },
    { to: '/about', label: 'Σχετικά', active: isAbout },
  ]

  return (
    <>
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-gray-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="h-16 flex items-center justify-between">
            {/* Logo */}
            <Link
              to="/"
              onClick={(e) => {
                if (path === '/') {
                  e.preventDefault()
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }
              }}
              className="flex items-center gap-1 flex-shrink-0 cursor-pointer"
            >
              <span className="text-lg sm:text-xl font-bold text-gray-900 hover:text-gray-700 transition-colors">
                Panellhnies<sup className="text-gray-700 font-bold">+</sup>
              </span>
            </Link>

            {/* Desktop nav (large screens only) */}
            <nav className="hidden lg:flex items-center gap-1">
              {LINKS.map(link =>
                link.ai ? (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`${linkBase} inline-flex items-center gap-1.5 font-medium ${
                      link.active
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-900 bg-gradient-to-r from-gray-100 to-gray-50 border border-gray-200 hover:from-gray-200 hover:to-gray-100'
                    }`}
                  >
                    <SparklesIcon className="h-4 w-4" />
                    {link.label}
                  </Link>
                ) : (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`${linkBase} ${link.active ? active : idle}`}
                  >
                    {link.label}
                  </Link>
                )
              )}
            </nav>

            {/* Mobile & tablet hamburger */}
            <button
              type="button"
              aria-label="Άνοιγμα μενού"
              aria-expanded={open}
              onClick={() => setOpen(true)}
              className="lg:hidden inline-flex items-center justify-center rounded-lg p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 cursor-pointer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-800" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeWidth="2" strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* ===== MOBILE / TABLET DRAWER ===== */}
      {open && (
        <div className="fixed inset-0 z-[999] lg:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-[1px] cursor-pointer"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />

          <aside className="absolute right-0 top-0 h-full w-[90vw] max-w-[420px] bg-white shadow-2xl border-l border-gray-200 rounded-l-2xl flex flex-col animate-[drawerIn_.22s_ease-out]">
            {/* Drawer header */}
            <div className="flex items-center justify-between h-16 px-4 border-b">
              <div className="flex items-center gap-2">
                <span className="text-lg font-semibold">Μενού</span>
              </div>
              <button
                aria-label="Κλείσιμο μενού"
                onClick={() => setOpen(false)}
                className="rounded-lg p-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500 cursor-pointer"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-800" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeWidth="2" strokeLinecap="round" d="M6 6l12 12M18 6l-12 12" />
                </svg>
              </button>
            </div>

            {/* Drawer links */}
            <nav className="px-4 py-3 space-y-2">
              {LINKS.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={
                    link.ai
                      ? `flex w-full items-center gap-2 px-4 py-3 rounded-lg text-base font-medium cursor-pointer ${link.active ? 'bg-gray-900 text-white' : 'text-gray-900 bg-gradient-to-r from-gray-100 to-gray-50 border border-gray-200'}`
                      : `block w-full px-4 py-3 rounded-lg text-base cursor-pointer ${link.active ? 'bg-gray-100 text-gray-700 font-medium' : 'text-gray-900 hover:bg-gray-100'}`
                  }
                >
                  {link.ai ? <SparklesIcon className="h-4 w-4" /> : null}
                  {link.label}
                </Link>
              ))}
            </nav>
          </aside>

          <style>{`
            @keyframes drawerIn {
              from { transform: translateX(100%); opacity: .7; }
              to   { transform: translateX(0);     opacity: 1;  }
            }
          `}</style>
        </div>
      )}
    </>
  )
}

export default Header
